// App.tsx
import React from 'react';
import { ArrowLeft, Github, ExternalLink, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Project6 from "@/assets/achivements/Project6.jpg";

const Inventory_Management_System: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Inventory Management System</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">React</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Node.js</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">MongoDB</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Express</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">REST API</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <img
              src={Project6}
              alt="Inventory Management System"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  A system designed to streamline stock tracking, optimize warehouse operations, and automate 
                  inventory updates using modern web technologies. The Inventory Management System provides 
                  businesses with a comprehensive solution for monitoring stock levels, tracking product movement, 
                  and generating insightful reports to inform purchasing decisions.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The system is built on a full-stack architecture with a React frontend and Node.js backend. 
                  The frontend provides an intuitive user interface for managing inventory, while the backend 
                  handles data processing, storage, and business logic. MongoDB serves as the database, storing 
                  product information, inventory levels, transaction history, and user data. The system uses REST 
                  APIs for communication between the frontend and backend, enabling real-time updates and 
                  seamless data flow.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Comprehensive dashboard with real-time inventory insights</li>
                  <li>Product categorization and organization with custom attributes</li>
                  <li>Low stock alerts and automated reordering</li>
                  <li>Barcode scanning integration for rapid check-in/check-out</li>
                  <li>Advanced search and filtering capabilities</li>
                  <li>Supplier management and purchase order generation</li>
                  <li>Detailed reporting with data visualization</li>
                  <li>Role-based access control for team members</li>
                  <li>Audit trail for tracking all system activities</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The frontend is built with React, utilizing a component-based architecture for maximum 
                  reusability and maintainability. State management is handled through Redux, ensuring a 
                  consistent application state across components. The backend is implemented with Node.js 
                  and Express, following RESTful API principles for scalable, maintainable service endpoints. 
                  MongoDB was chosen for its flexibility in handling varied product data structures and its 
                  scalability to accommodate growing inventory. Authentication is implemented using JWT tokens, 
                  and all API endpoints are secured with appropriate middleware to ensure data protection.
                </p>
              </div>
              
              {/* Project demo */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Documentation</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-white/5 p-6">
                  <h3 className="text-xl font-medium mb-4">System Architecture</h3>
                  <div className="space-y-4">
                    <div className="rounded-md bg-white/10 p-4">
                      <h4 className="font-medium mb-2">Frontend (React)</h4>
                      <ul className="list-disc list-inside text-sm">
                        <li>React Components</li>
                        <li>Redux State Management</li>
                        <li>Material UI Components</li>
                        <li>Chart.js for Data Visualization</li>
                      </ul>
                    </div>
                    <div className="rounded-md bg-white/10 p-4">
                      <h4 className="font-medium mb-2">Backend (Node.js, Express)</h4>
                      <ul className="list-disc list-inside text-sm">
                        <li>RESTful API Endpoints</li>
                        <li>JWT Authentication</li>
                        <li>Middleware for Validation</li>
                        <li>Business Logic Controllers</li>
                      </ul>
                    </div>
                    <div className="rounded-md bg-white/10 p-4">
                      <h4 className="font-medium mb-2">Database (MongoDB)</h4>
                      <ul className="list-disc list-inside text-sm">
                        <li>Product Collections</li>
                        <li>Inventory Transactions</li>
                        <li>User Management</li>
                        <li>Reporting Aggregations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/Inventory-Management-System" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/Inventory-Management-System/blob/main/README.md" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <ExternalLink className="mr-2 h-5 w-5" />
                      <span>Documentation</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">R</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">React</p>
                      <p className="text-sm text-muted-foreground">Frontend library</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">N</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Node.js</p>
                      <p className="text-sm text-muted-foreground">Backend runtime</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">M</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">MongoDB</p>
                      <p className="text-sm text-muted-foreground">NoSQL database</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">E</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Express</p>
                      <p className="text-sm text-muted-foreground">Web framework</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Highlights</h3>
                <p className="text-muted-foreground mb-4">
                  The Inventory Management System provides comprehensive stock control with intuitive interfaces 
                  and powerful backend processing for businesses of all sizes.
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Code Quality</span>
                    <span className="text-primary font-medium">High</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[85%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Scalability</span>
                    <span className="text-primary font-medium">Excellent</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[90%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
            
            <Link to="/Avinyaa_2025" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Avinyaa 2025 Website</h3>
              <p className="text-muted-foreground line-clamp-2">A website for a college symposium with modern web technologies.</p>
            </Link>
            
            <Link to="/Smart_Irrigation_System_using_Precision_Farming" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Smart Irrigation System</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes water usage in agriculture by monitoring soil moisture levels.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Inventory_Management_System;
